import styled from '@emotion/styled';
import Head from 'next/head';
import Router from 'next/router';
import NProgress from 'nprogress';
import { GA_TRACKING_PARAMS_NAME } from '@widget/consts';
import config from 'next/config';
import { SaveOriginalReferralScript } from '@widget/core';

import { breadCrumbElementsProps, generateBreadCrumbPropsFromLayer, mkAssetUrl } from '@lib/utils';
import appleTouchImg from '@static/apple-touch-icon.png';
import faviconImg from '@static/favicon.png';
import getLayerStore from '@stores/LayerStore';

import type { FC, PropsWithChildren } from 'react';
import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';
import type { LayerInfo } from '@lib/types';

const LayerStore = getLayerStore();
const LayoutWrapper = styled.div`
  background: #f2f2f2;

  .scroll-up {
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    max-width: 150px;
    padding: 0 1em;
    z-index: 10;
    outline: none;

    &.hide {
      display: none;
    }

    &:hover {
      background: ${(props) => props.theme.colors.yellow._400};
      color: ${(props) => props.theme.colors.primary};
    }

    @media (min-width: ${(props) => props.theme.breakpoints.mobile_landscape}) {
      display: none;
    }
  }
`;

Router.events.on('routeChangeStart', () => {
  NProgress.start();
});

Router.events.on('routeChangeComplete', () => {
  LayerStore.areaClicked = false;
  setTimeout(() => {
    NProgress.done();
  }, 500);
});

Router.events.on('routeChangeError', () => {
  NProgress.done();
});

type Props = PropsWithChildren<{
  url: string;
  title?: string;
  sharingImage?: string;
  metaDescription?: string;
  gtmId?: string;
  gaMeasurementId?: string;
  layer: LayerInfo;
  canonicalUrl?: string;
}>;

export const Layout: FC<Props> = ({
  children,
  title = 'McMakler',
  sharingImage,
  metaDescription,
  gtmId,
  gaMeasurementId,
  layer,
  canonicalUrl,
}) => {
  const itemListElement: ListItem[] = [];
  const breadCrumbProps = generateBreadCrumbPropsFromLayer(layer);
  const hostName = config().publicRuntimeConfig.hostName;
  breadCrumbElementsProps(breadCrumbProps, false).forEach((v, i) => {
    itemListElement.push({
      '@type': 'ListItem',
      position: i + 1,
      name: v.text,
      item: `${hostName}${v.link || ''}`,
    });
  });

  const bcList: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };

  return (
    <LayoutWrapper>
      <Head>
        <title>{title}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <link rel="shortcut icon" type="image/x-icon" href={faviconImg} />
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <meta name="theme-color" content="#000000" />
        <link rel="apple-touch-icon" sizes="180x180" href={appleTouchImg} />
        {process.env.NODE_ENV === 'production' && (
          <link rel="manifest" href={mkAssetUrl('/static/manifest.json')} />
        )}
        {!!canonicalUrl && <link rel="canonical" href={`${canonicalUrl}`} />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
        <meta property="og:image" content={sharingImage} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={title} />
        <meta name="twitter:image" content={sharingImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script
          id="usercentrics-cmp"
          src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
          data-settings-id="UzITbKDdP"
          async
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(bcList) }}
        />
        <script
          type="text/plain"
          data-usercentrics="AB Tasty"
          src="https://try.abtasty.com/253e52effaffc100130f829ada2d6369.js"
        ></script>
        <script
          src={mkAssetUrl('/static/scripts/gtag.js')}
          data-ga-tracking-params-name={GA_TRACKING_PARAMS_NAME}
          data-ga-measurement-id={gaMeasurementId}
          defer
        />
        <script
          type="text/plain"
          data-usercentrics="Google Tag Manager"
          src={mkAssetUrl('/static/scripts/gtm.js')}
          data-gtm-id={gtmId}
          defer
        />
        <script
          data-usercentrics="Trustpilot"
          type="text/plain"
          src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          async
        ></script>
        <SaveOriginalReferralScript />
      </Head>
      {children}
      <noscript>
        <iframe
          title="Google tag manager"
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </LayoutWrapper>
  );
};
